import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardActions,
    CardContent,
    Chip,
    Dialog,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { RoadmapItem } from '@/models/RoadmapItem';
import { STATUS } from '@/constants/status';
import { getColorPaletteForStatus } from '@/utils/color-utils';
import ImageVideo from './ImageVideo';
import StatusChip from './StatusChip';
import LikeButton from './LikeButton';
import FollowButton from './FollowButton';
import Flyout from './Flyout';
import cwLogo from '@/assets/cw_logo.svg';
import cwLogoDark from '@/assets/cwlogo_dark.svg';
import cwLogoLight from '@/assets/cwlogo_light.svg';
import releaseNotes from '@/assets/release_notes.svg';
import './GridCard.css';

interface GridCardProps {
    data: RoadmapItem;
    onLike: (jiraId: string, isLiked: boolean) => void;
    onFollow: (jiraId: string, isFollowed: boolean) => void;
    isAuthenticated?: boolean;
    selectedJiraId?: string | null;
    onFlyoutClose?: () => void;
}

const GridCard: React.FC<GridCardProps> = ({
    data,
    onLike,
    onFollow,
    isAuthenticated = true,
    selectedJiraId,
    onFlyoutClose,
}) => {
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);

    useEffect(() => {
        if (selectedJiraId && data.jiraId === selectedJiraId) {
            setIsFlyoutOpen(true);
        }
    }, [selectedJiraId, data.jiraId]);

    const color = getColorPaletteForStatus(data.statusOnUI);
    const logoSrc = data.statusOnUI === STATUS.NEW_RELEASE ? cwLogoDark : cwLogoLight;

    const renderVideo = (videoId: string) => {
        return (
            <>
                <img
                    src={`https://img.youtube.com/vi/${videoId}/0.jpg`}
                    alt="video thumbnail"
                    className="grid-card-thumbnail"
                />
                <PlayCircleIcon className="grid-card-play-icon" />
            </>
        );
    };

    const renderImage = (imageSrc: string) => {
        return (
            <img
                src={imageSrc}
                alt="Card image"
                className={`grid-card-image ${data.videoOrImageLink ? 'grid-card-image-full' : 'grid-card-image-logo'}`}
            />
        );
    };

    const renderImageOrVideo = (videoId: string | null) => {
        const videoOrImageLink = data.videoOrImageLink;
        if (!videoOrImageLink) return renderImage(logoSrc);

        const isVideo = videoOrImageLink.includes('youtube') || videoOrImageLink.includes('youtu.be');
        if (!isVideo) return renderImage(videoOrImageLink);

        return videoId ? renderVideo(videoId) : renderImage(logoSrc);
    };

    const handleFlyoutOpen = () => {
        setIsFlyoutOpen(true);
    };

    const handleFlyoutClose = () => {
        setIsFlyoutOpen(false);
        onFlyoutClose?.();
    };

    const handleCommunityClick = () => {
        window.open(data.virtualCommunity, '_blank');
    };

    const handleReleaseNotesClick = () => {
        window.open(data.releaseNotes, '_blank');
    };

    const showCommunityLink = data.virtualCommunity !== '' &&
        data.virtualCommunity.startsWith('https://virtualcommunity.connectwise.com');

    const itemWithColor = { ...data, color };

    return (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }} className="grid-card-container">
            <ImageVideo videoUrl={data.videoOrImageLink}>
                {({ handleOpen, videoId }) => (
                    <Card className={`grid-card grid-card-border-${color}`}>
                        <Box
                            className={`grid-card-media grid-card-media-${color}`}
                            onClick={videoId ? handleOpen : undefined}
                            style={{ cursor: videoId ? 'pointer' : 'default' }}
                        >
                            {renderImageOrVideo(videoId)}
                        </Box>

                        <CardContent className="grid-card-content">
                            <Box className="grid-card-header">
                                <Typography variant="subtitle2" className="grid-card-category">
                                    {data.category}
                                </Typography>
                                <StatusChip data={itemWithColor} />
                            </Box>
                            <Typography variant="h6" className="grid-card-title">
                                {data.title}
                            </Typography>
                            <Typography className="grid-card-description">
                                {data.description}
                            </Typography>
                            <Box className="grid-card-product-row">
                                <Chip
                                    label={data.product}
                                    size="small"
                                    className="grid-card-product-chip"
                                />
                            </Box>
                        </CardContent>

                        <CardActions className="grid-card-actions">
                            {isAuthenticated && (
                                <LikeButton data={data} onLike={onLike} variant="grid" />
                            )}
                            {isAuthenticated && (
                                <FollowButton data={data} onFollow={onFollow} variant="grid" />
                            )}

                            {showCommunityLink && (
                                <Tooltip title="Community" placement="top">
                                    <Box className="grid-card-link-container">
                                        <img
                                            src={cwLogo}
                                            alt="Community"
                                            onClick={handleCommunityClick}
                                            className="grid-card-community-icon"
                                        />
                                    </Box>
                                </Tooltip>
                            )}

                            {data.releaseNotes !== '' && (
                                <Tooltip title="Release Notes" placement="top">
                                    <Box className="grid-card-link-container grid-card-release-notes">
                                        <img
                                            src={releaseNotes}
                                            alt="Release Notes"
                                            onClick={handleReleaseNotesClick}
                                            className="grid-card-release-icon"
                                        />
                                    </Box>
                                </Tooltip>
                            )}

                            <IconButton
                                aria-label="View details"
                                size="small"
                                className="grid-card-expand-button"
                                onClick={handleFlyoutOpen}
                            >
                                <AddOutlinedIcon />
                            </IconButton>

                            {isFlyoutOpen && (
                                <Dialog
                                    open={isFlyoutOpen}
                                    onClose={handleFlyoutClose}
                                    className="grid-card-dialog"
                                    PaperProps={{ className: 'grid-card-dialog-paper' }}
                                >
                                    <Flyout
                                        onClose={handleFlyoutClose}
                                        data={itemWithColor}
                                        onLike={onLike}
                                        onFollow={onFollow}
                                        isAuthenticated={isAuthenticated}
                                    />
                                </Dialog>
                            )}
                        </CardActions>
                    </Card>
                )}
            </ImageVideo>
        </Grid>
    );
};

export default GridCard;
